/**
 * FluxNodeStatusWidget Component
 *
 * Compact widget showing FluxNode status for the Portfolio Dashboard
 * - Shows node count by tier
 * - Shows overall status (all online / some issues)
 * - VIP badge for users with nodes hosted via our hosting service
 * - Clickable to navigate to FluxNode page
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { useFluxNodes, useWalletBalances } from '../../hooks/useFluxNodes';
import { fluxNodeAPI } from '../../services/fluxNodeApi';
import { TIER_REQUIREMENTS } from '../../types/fluxNode.types';
import { MONITOR_SYNC_EVENT, STORAGE_KEYS } from '../../hooks/useMonitorWalletsSync';

const WALLETS_STORAGE_KEY = STORAGE_KEYS.flux.wallets;
const HOSTED_NODES_STORAGE_KEY = 'fluxnode_hosted_nodes';

// Tier colors matching the FluxNode page style
const TIER_COLORS = {
  CUMULUS: '#3b82f6',
  NIMBUS: '#8b5cf6',
  STRATUS: '#ec4899',
};

interface FluxNodeStatusWidgetProps {
  onNavigate?: (pageId: string) => void;
}

export function FluxNodeStatusWidget({ onNavigate }: FluxNodeStatusWidgetProps) {
  const { t } = useTranslation();
  const [wallets, setWallets] = useState<string[]>([]);
  const [hasHostedNodes, setHasHostedNodes] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Load wallets from localStorage
  const loadWallets = useCallback(() => {
    const savedWallets = localStorage.getItem(WALLETS_STORAGE_KEY);
    if (savedWallets) {
      try {
        const parsed = JSON.parse(savedWallets);
        if (Array.isArray(parsed)) {
          setWallets(parsed);
        }
      } catch (e) {
        console.error('Error loading FluxNode wallets:', e);
      }
    }

    // Check if user has any nodes hosted with us
    const savedHostedNodes = localStorage.getItem(HOSTED_NODES_STORAGE_KEY);
    if (savedHostedNodes) {
      try {
        const hostedNodes = JSON.parse(savedHostedNodes);
        // Check for active or pending hosted nodes
        setHasHostedNodes(Array.isArray(hostedNodes) && hostedNodes.some(
          (n: { status: string }) => n.status === 'active' || n.status === 'pending'
        ));
      } catch (e) {
        console.error('Error loading hosted nodes:', e);
      }
    }
  }, []);

  // Load wallets on mount and when sync completes
  useEffect(() => {
    loadWallets();

    // Listen for monitor sync events to reload wallets
    const handleSyncComplete = () => {
      console.log('[FluxNodeWidget] Sync event received, reloading wallets...');
      loadWallets();
    };

    window.addEventListener(MONITOR_SYNC_EVENT, handleSyncComplete);
    return () => {
      window.removeEventListener(MONITOR_SYNC_EVENT, handleSyncComplete);
    };
  }, [loadWallets]);

  // Fetch nodes using the same hook as FluxNodeMonitor
  const { nodes, isLoading, totalNodes, activeNodes, nodesByTier } = useFluxNodes(wallets);

  // Fetch wallet balances for accumulated rewards calculation
  const { data: walletBalances } = useWalletBalances(wallets);

  // Calculate accumulated rewards (wallet balance - locked collateral)
  const totalCollateral = nodes.reduce((sum, node) => {
    return sum + (TIER_REQUIREMENTS[node.tier]?.collateral || 0);
  }, 0);
  const totalWalletBalance = walletBalances?.reduce((sum, wb) => sum + wb.balance, 0) ?? 0;
  const accumulatedRewards = totalWalletBalance - totalCollateral;

  // Check for VIP wallet addresses (special customers)
  const isVIPWallet = wallets.some(wallet => wallet.toLowerCase().endsWith('ri98'));

  const handleClick = () => {
    if (onNavigate) {
      onNavigate('fluxnode-monitor');
    }
  };

  const allOnline = totalNodes > 0 && activeNodes === totalNodes;
  const hasIssues = totalNodes > 0 && activeNodes < totalNodes;
  const isConfigured = wallets.length > 0;

  // Show skeleton/placeholder if not configured
  if (!isConfigured) {
    return (
      <div
        onClick={handleClick}
        style={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.06), rgba(99, 102, 241, 0.04))',
          borderRadius: '12px',
          padding: '12px 16px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          border: '1px dashed rgba(139, 92, 246, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '200px',
          opacity: 0.7,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.7';
          e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.3)';
        }}
        title={t('fluxNode.widget.clickToConfigure', 'Cliquer pour configurer FluxNode Monitor')}
      >
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: 'rgba(139, 92, 246, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icons.Flux size={18} color="rgba(43, 97, 209, 0.5)" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)', opacity: 0.6 }}>
              FluxNodes
            </span>
          </div>
          <span style={{ fontSize: '11px', color: 'var(--muted)', opacity: 0.6 }}>
            {t('fluxNode.widget.clickToConfigure', 'Cliquer pour configurer')}
          </span>
        </div>
        <Icons.Plus size={16} style={{ color: 'rgba(139, 92, 246, 0.5)', flexShrink: 0 }} />
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      style={{
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12), rgba(99, 102, 241, 0.08))',
        borderRadius: '12px',
        padding: '12px 16px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: '1px solid rgba(139, 92, 246, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        minWidth: '200px',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.2)';
        e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.4)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.2)';
      }}
      title={t('fluxNode.widget.clickToOpen', 'Cliquer pour ouvrir FluxNode Monitor')}
    >
      {/* VIP Badge - for users with nodes hosted via our service */}
      <div
        style={{
          position: 'absolute',
          top: '-6px',
          right: '8px',
          padding: '2px 8px',
          borderRadius: '8px',
          fontSize: '9px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          background: (hasHostedNodes || isVIPWallet)
            ? 'linear-gradient(135deg, #FFD700, #FFA500)'
            : 'rgba(100, 100, 100, 0.4)',
          color: (hasHostedNodes || isVIPWallet) ? '#1a1a2e' : 'rgba(150, 150, 150, 0.7)',
          boxShadow: (hasHostedNodes || isVIPWallet)
            ? '0 2px 8px rgba(255, 215, 0, 0.4)'
            : 'none',
          transition: 'all 0.3s ease',
          cursor: 'help',
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <Icons.Crown size={10} />
        VIP
        {/* Tooltip */}
        {showTooltip && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              padding: '8px 12px',
              background: 'var(--card-bg, #1a1a2e)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              whiteSpace: 'nowrap',
              fontSize: '11px',
              fontWeight: 400,
              color: 'var(--text)',
              textTransform: 'none',
              letterSpacing: 'normal',
              zIndex: 1000,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {(hasHostedNodes || isVIPWallet) ? (
              <span style={{ color: '#10b981' }}>
                ✅ {t('fluxNode.widget.hostedBadge', 'Hébergé via notre service')}
              </span>
            ) : (
              <span style={{ color: 'var(--muted)' }}>
                {t('fluxNode.widget.hostedBadgeInactive', 'Badge réservé aux clients hébergés chez nous')}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Icon */}
      <div
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icons.Flux size={18} color="white" />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>
            FluxNodes
          </span>
          {/* Status indicator */}
          {isLoading ? (
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#6b7280',
                animation: 'pulse 1s infinite',
              }}
            />
          ) : (
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: allOnline ? '#10b981' : hasIssues ? '#f59e0b' : '#6b7280',
                boxShadow: allOnline ? '0 0 8px #10b981' : 'none',
                animation: allOnline ? 'pulse 2s infinite' : 'none',
              }}
            />
          )}
        </div>

        {/* Node counts by tier + accumulated rewards */}
        {isLoading ? (
          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
            Chargement...
          </span>
        ) : totalNodes > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {(['CUMULUS', 'NIMBUS', 'STRATUS'] as const).map(tier => {
              const count = nodesByTier[tier];
              if (count === 0) return null;

              // Count online nodes for this tier (use same logic as useFluxNodes hook)
              const onlineCount = nodes.filter(
                n => n.tier === tier && fluxNodeAPI.getNodeStatus(n) === 'CONFIRMED'
              ).length;

              return (
                <div
                  key={tier}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <span
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '2px',
                      background: TIER_COLORS[tier],
                    }}
                  />
                  <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                    {onlineCount}/{count}
                  </span>
                </div>
              );
            })}
            {/* Accumulated rewards display - matching HBAR staking widget style */}
            {totalWalletBalance > 0 && (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                title={t('fluxNode.widget.accumulatedRewards', 'Rewards FLUX cumulés (solde - collatéral)')}
              >
                <Icons.Gift size={10} style={{ color: accumulatedRewards >= 0 ? '#10b981' : '#ef4444' }} />
                <span style={{ fontSize: '11px', color: accumulatedRewards >= 0 ? '#10b981' : '#ef4444' }}>
                  {accumulatedRewards >= 0 ? '+' : ''}{accumulatedRewards.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
              </div>
            )}
          </div>
        ) : (
          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
            {t('fluxNode.widget.noNodes', 'Aucun node détecté')}
          </span>
        )}
      </div>

      {/* Arrow */}
      <Icons.ChevronRight size={16} style={{ color: 'var(--muted)', flexShrink: 0 }} />

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
